<template>
    <Section class="contact-form">
        <div class="form-container">
            <div v-if="!success" class="form-header">
                <h2>{{ formHeader }}</h2>
                <div 
                    v-if="text"
                    class="rich-text" 
                    v-html="text"
                />
            </div>
            <div 
                v-if="success" 
                ref="formSent"
                class="h3 form-success-message"
            >
                <div class="success-icons">
                    <i class="fas fa-bars-sort wind-icon" />
                    <i class="fas fa-envelope-circle-check fa-lg envelope-icon" />
                </div>
                {{ $translate('contact_form_success_message', 'Tack för ditt meddelande!') }}
            </div>
            <form v-else class="form" novalidate>
                <dynamic-input
                    v-for="(config, inputKey) in inputs"
                    :key="inputKey"
                    class="form-input"
                    :form-key="config.formKey"
                    :type="config.type"
                    :label="config.label"
                    :value="config.value"
                    :placeholder="config.placeholder"
                    :error-text="config.errorText"
                    :error="showErrors && !config.validate(config.value)"
                    @set="(_, input) => config.value = input"
                />
                <Button
                    :text="$translate('contact_form_submit_button_text', 'Skicka in')"
                    size="sm"
                    class="submit-button"
                    background="green"
                    @click="submit()"
                />
                <div v-if="generalError" class="form-general-error">
                    {{ $translate('contact_form_general_error', 'Oops, något gick fel!') }}
                </div>
            </form>
        </div>
    </Section>
</template>

<script>
import DynamicInput from './DynamicInput.vue';
export default {
    components: { DynamicInput },
    props: {
        header: {
            type: String,
            required: false,
            default: null
        },
        text: {
            type: String,
            required: false,
            default: null
        }
    },
    data() {
        return {
            success: false,
            showErrors: false,
            generalError: false,
            inputs: {
                email: {
                    type: 'email',
                    label: this.$translate('input_email_label', 'E-post'),
                    formKey: 'email',
                    placeholder: this.$translate('input_email_placeholder', 'john.doe@gmail.com'),
                    value: null,
                    errorText: this.$translate('input_email_error', 'Vänligen fyll i en giltig email'),
                    validate: (input) => {
                        const reg = /^[a-zA-Z0-9.!#$%&'*+/=?^_`{|}~-]+@[a-zA-Z0-9-]+(?:\.[a-zA-Z0-9-]+)+$/;
                        return input && reg.test(input);
                    }
                },
                name: {
                    type: 'text',
                    label: this.$translate('input_name_label', 'Namn'),
                    formKey: 'name',
                    placeholder: this.$translate('input_name_placeholder', 'John Doe'),
                    value: null,
                    errorText: this.$translate('input_name_error', 'Vänligen fyll i ditt namn'),
                    validate: (input) => input && input.length > 1
                },
                message: {
                    type: 'textarea',
                    label: this.$translate('input_message_label', 'Meddelande'),
                    formKey: 'message',
                    placeholder: this.$translate('input_message_placeholder', 'Ditt meddelande ...'),
                    value: null,
                    errorText: this.$translate('input_message_error', 'Vänligen fyll i ditt meddelande'),
                    validate: (input) => input && input.length > 1
                },
            }
        };
    },
    computed: {
        formHeader() {
            return this.header 
                ? this.header 
                : this.$translate('contact_form_default_header', 'Kontakta oss');
        },
        
        validateInputs() {
            return Object.keys(this.inputs).every((inputKey) => 
                this.inputs[inputKey].validate(this.inputs[inputKey].value)
            );
        }
    },
    methods: {
        submit() {
            this.showErrors = true;

            const payload = {
                email: this.inputs.email.value,
                name: this.inputs.name.value,
                message: this.inputs.message.value,
                site: `kreditkort.com | ${process.env.PRISMIC_LANG}`
            };
            if (! this.validateInputs) {
                return;
            }
            this.$axios.post(process.env.FORMSPREE_URL, payload)
                .then(() => {
                    this.success = true;
                    this.$nextTick(() => {
                        const el = this.$refs.formSent;
                        if (el) {
                            el.scrollIntoView({ behavior: 'smooth', block: 'center' });
                        }
                    });

                })
                .catch((e) => {
                    console.log(e);
                    this.generalError = true;
                });
        }
    }
};
</script>
<style lang="scss" scoped>
    .contact-form::v-deep {
        .form-container {
            box-shadow: $bxs;
            padding: 1.25rem;
            @include border-radius(3px);
            @include device(pad) {
                padding: 2.5rem;
            }
            .form-header {
                margin-bottom: 1.75rem;
                @include device(pad) {
                    margin-bottom: 2rem;
                }
            }
            .form-success-message {
                display: flex;
                flex-direction: column;
                align-items: center;
                justify-content: center;
                gap: 1rem;

                .success-icons {
                    display: flex;
                    align-items: center;
                    justify-content: center;
                    .wind-icon {
                        rotate: -180deg;
                        opacity: 0.25;
                    }
                    .envelope-icon {
                        color: $pink;
                    }
                }
            }
            .form {
                .form-input {
                    margin-bottom: 1.75rem;
                }
    
                .submit-button {
                    width: 100%;
                    @include border-radius(3px);
                }
            }
            .form-general-error {
                font-family: $fontText;
                color: #8b0000;
                background: rgba(179,0,0,.15);
                padding: 7px;
                font-size: 13px;
                margin-top: -5px;
            }
        }
    }
</style>